import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  ClipboardCopy,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormAlert,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { compareMoney, formatPrice } from '../../../utils/money';
import type {
  AdminWalletDeposit,
  AdminWalletDepositRefundRequest,
  AdminWalletDepositRefundResult,
} from '../../../types/models/Wallet';
import type { ApiResponseAdminWalletDepositRefund } from '../../../types/api/ApiResponseAdminWallet';
import { normalisePositiveAmount, refundRefusal, refundableRemainder } from './walletUtils';
import type { RefundRefusal } from './walletUtils';

interface WalletRefundModalProps {
  /**
   * The top-up AS THE PAGE DISPLAYED IT when Refund was clicked — a snapshot, so a reload of the page
   * while the dialog is open never changes the `expected_clawback_amount` this form sends.
   */
  deposit: AdminWalletDeposit;
  clientId: number;
  clientName: string;
  onClose: () => void;
  /** Re-read the wallet: after a recorded refund, or when the top-up changed meanwhile. */
  onReload: () => void;
}

interface FieldErrors {
  amount?: string;
  description?: string;
  allow_negative?: string;
}

/**
 * Refund (part of) a credited top-up — `POST /admin/wallets/{clientId}/deposits/{depositId}/refund`
 * (docs/api-types/admin-wallets.md, D11 revised 2026-10-18). Record the refund here FIRST, then refund
 * exactly that amount in the Mollie dashboard (the response's `next_step`): Mollie's refund webhook then
 * finds nothing left to debit. The other way round (a negative adjustment plus a Mollie refund) debits the
 * customer twice.
 *
 * Double submission is refused by the backend through `expected_clawback_amount` (the figure this row
 * displayed). This form never re-posts on its own: a « changed meanwhile » refusal reloads the page and
 * disables Refund — staff reopens the dialog from the refreshed row.
 */
const WalletRefundModal: React.FunctionComponent<WalletRefundModalProps> = ({
  deposit,
  clientId,
  clientName,
  onClose,
  onReload,
}) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const remainder = useMemo(() => refundableRemainder(deposit) ?? '0.00', [deposit]);
  const [amountInput, setAmountInput] = useState(remainder);
  const [description, setDescription] = useState('');
  const [allowNegative, setAllowNegative] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<{ message: string } & RefundRefusal>();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [result, setResult] = useState<AdminWalletDepositRefundResult | null>(null);

  const amount = normalisePositiveAmount(amountInput);
  const overRemainder = amount !== null && (compareMoney(amount, remainder) ?? 1) > 0;
  const trimmedDescription = description.trim();
  const descriptionInvalid = trimmedDescription.length > 255;
  const stale = refusal?.stale === true;
  const canSubmit =
    !saving &&
    !stale &&
    result === null &&
    amount !== null &&
    !overRemainder &&
    trimmedDescription.length >= 1 &&
    !descriptionInvalid;

  const amountHelper =
    fieldErrors.amount ??
    (amountInput.trim() !== '' && amount === null
      ? 'Enter a positive amount with at most 2 decimals, e.g. 40 or 12.50.'
      : overRemainder
        ? `At most ${formatPrice(remainder)} — what is left to refund on this top-up.`
        : null);
  const descriptionHelper = fieldErrors.description ?? (descriptionInvalid ? 'At most 255 characters.' : null);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const submit = async () => {
    if (!canSubmit || amount === null) {
      return;
    }
    setSaving(true);
    setRefusal(undefined);
    setFieldErrors({});
    const payload: AdminWalletDepositRefundRequest = {
      amount,
      expected_clawback_amount: deposit.clawback_amount,
      description: trimmedDescription,
      allow_negative: allowNegative,
    };
    try {
      const response = await apiFetch(`/admin/wallets/${clientId}/deposits/${deposit.id}/refund`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // JSend: branch on body.status, never response.ok — every refusal is an `error` at HTTP 200.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletDepositRefund | null;
      const d = body?.data;
      if (body?.status === 'success' && d?.transaction && d.deposit && typeof d.next_step === 'string') {
        setResult(d as AdminWalletDepositRefundResult);
        addNotification({
          title: 'Refund recorded — now refund it at Mollie',
          srTitle: 'Success alert',
          description: `${formatPrice(amount)} taken back from ${clientName}'s wallet. ${d.next_step}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onReload();
        return;
      }
      const message =
        d?.expected_clawback_amount ?? d?.body ?? body?.message ?? 'Could not record the refund. Please try again.';
      const outcome = refundRefusal(message);
      setRefusal({ message, ...outcome });
      setFieldErrors({ amount: d?.amount, description: d?.description, allow_negative: d?.allow_negative });
      if (outcome.stale) {
        onReload();
      }
      addNotification({
        title: 'Refund not recorded',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      // A lost response: the refund may or may not be recorded. Never re-post from this form — reload
      // and look (a resubmission would be refused anyway, the expected figure being stale).
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setRefusal({
        message,
        hint: `The answer was lost, so the refund may or may not have been recorded, and the page has been reloaded. If the top-up now shows ${formatPrice(amount)} more taken back, the refund was recorded: refund exactly that at Mollie on payment ${deposit.mollie_payment_id ?? '—'}. Otherwise, do nothing at Mollie.`,
        stale: true,
        suggestReceivable: false,
      });
      onReload();
      addNotification({
        title: 'Refund status unknown',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const renderResult = (r: AdminWalletDepositRefundResult) => (
    <>
      <Alert variant="success" isInline title="Refund recorded — one step left, at Mollie" className="pf-v6-u-mb-md">
        <Content component="p">
          <strong>{r.next_step}.</strong>
        </Content>
        <Content component="p">
          Do it now in the Mollie dashboard, for exactly this amount. Mollie&apos;s refund webhook will then find the
          money already taken back and debit nothing more. A refund recorded here but never made at Mollie leaves the
          customer debited for money they did not get back.
        </Content>
      </Alert>
      <DescriptionList isCompact isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>Mollie payment</DescriptionListTerm>
          <DescriptionListDescription>
            <ClipboardCopy
              isReadOnly
              hoverTip="Copy"
              clickTip="Copied"
              copyAriaLabel="Copy the Mollie payment id"
              textAriaLabel="Mollie payment id"
            >
              {r.deposit.mollie_payment_id}
            </ClipboardCopy>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Refunded on this top-up</DescriptionListTerm>
          <DescriptionListDescription>
            {formatPrice(r.deposit.clawback_amount)} of {formatPrice(r.deposit.amount)} (
            {formatPrice(r.deposit.refundable_remaining)} left)
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>New wallet balance</DescriptionListTerm>
          <DescriptionListDescription>{formatPrice(r.balance)}</DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
    </>
  );

  const renderForm = () => (
    <Form
      id="wallet-refund-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {refusal && (
        <FormAlert>
          <Alert variant="danger" isInline isLiveRegion title={refusal.message}>
            {refusal.hint}
          </Alert>
        </FormAlert>
      )}
      <Content component="p">
        Record the refund here <strong>first</strong>, then refund exactly that amount at Mollie — the next step is
        shown once it is recorded.
      </Content>
      <DescriptionList isCompact isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>Top-up</DescriptionListTerm>
          <DescriptionListDescription>
            #{deposit.id} · {formatPrice(deposit.amount)} · <code>{deposit.mollie_payment_id}</code>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Already taken back</DescriptionListTerm>
          <DescriptionListDescription>{formatPrice(deposit.clawback_amount)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Left to refund</DescriptionListTerm>
          <DescriptionListDescription>{formatPrice(remainder)}</DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      <FormGroup label="Amount" isRequired fieldId="wallet-refund-amount">
        <TextInput
          id="wallet-refund-amount"
          value={amountInput}
          onChange={(_e, v) => setAmountInput(v)}
          validated={amountHelper ? 'error' : 'default'}
          isDisabled={saving || stale}
          aria-describedby="wallet-refund-amount-helper"
        />
        <FormHelperText>
          <HelperText id="wallet-refund-amount-helper">
            <HelperTextItem variant={amountHelper ? 'error' : 'default'}>
              {amountHelper ?? `In EUR, at most ${formatPrice(remainder)}.`}
            </HelperTextItem>
          </HelperText>
        </FormHelperText>
      </FormGroup>
      <FormGroup label="Reason" isRequired fieldId="wallet-refund-description">
        <TextInput
          id="wallet-refund-description"
          value={description}
          onChange={(_e, v) => setDescription(v)}
          validated={descriptionHelper ? 'error' : 'default'}
          isDisabled={saving || stale}
          aria-describedby="wallet-refund-description-helper"
          placeholder="Remboursement partiel du rechargement (demande du client)"
        />
        <FormHelperText>
          <HelperText id="wallet-refund-description-helper">
            <HelperTextItem variant={descriptionHelper ? 'error' : 'default'}>
              {descriptionHelper ?? 'The statement line the customer sees (1–255 characters).'}
            </HelperTextItem>
          </HelperText>
        </FormHelperText>
      </FormGroup>
      <FormGroup fieldId="wallet-refund-allow-negative">
        <Checkbox
          id="wallet-refund-allow-negative"
          label="Record as receivable"
          description="Let the balance go below zero when the customer has already spent this money."
          isChecked={allowNegative}
          onChange={(_e, checked) => setAllowNegative(checked)}
          isDisabled={saving || stale}
        />
        {(fieldErrors.allow_negative || refusal?.suggestReceivable) && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant={fieldErrors.allow_negative ? 'error' : 'warning'}>
                {fieldErrors.allow_negative ?? 'Needed here: the balance does not cover this refund.'}
              </HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
    </Form>
  );

  return (
    <Modal
      variant="medium"
      isOpen
      onClose={handleClose}
      aria-labelledby="wallet-refund-title"
      aria-describedby="wallet-refund-subtitle"
    >
      <ModalHeader
        title={result ? 'Refund recorded' : 'Refund a top-up'}
        labelId="wallet-refund-title"
        description={`Prepaid wallet of ${clientName}`}
        descriptorId="wallet-refund-subtitle"
      />
      <ModalBody>{result ? renderResult(result) : renderForm()}</ModalBody>
      <ModalFooter>
        {result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="danger" type="submit" form="wallet-refund-form" isLoading={saving} isDisabled={!canSubmit}>
              Record refund
            </Button>
            <Button variant="link" onClick={handleClose} isDisabled={saving}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default WalletRefundModal;
